import { useEffect, useRef, useState } from "react";
import { motion, animate, useMotionValue } from "framer-motion";
import { Activity, TrendingUp, Target } from "lucide-react";

/**
 * Self-playing, looping demo: a patient's rehab journey advances across sessions —
 * pain drops, ROM improves, goals fill in. Notation's UI language, no interaction.
 */

interface Session {
  tab: string;
  status: string;
  statusColor: string;
  phase: string;
  pain: number;
  flexion: number;
  abduction: number;
  flexionDelta: number | null;
  abductionDelta: number | null;
  goal: string;
  plan: string[];
}

const SESSIONS: Session[] = [
  {
    tab: "baseline",
    status: "BASELINE ESTABLISHED",
    statusColor: "#495B25",
    phase: "Baseline",
    pain: 6,
    flexion: 140,
    abduction: 130,
    flexionDelta: null,
    abductionDelta: null,
    goal: "Improve right shoulder flexion to 150° for overhead reaching with less pain.",
    plan: [],
  },
  {
    tab: "update",
    status: "ON TRACK",
    statusColor: "#166534",
    phase: "Early Rehab",
    pain: 5,
    flexion: 155,
    abduction: 145,
    flexionDelta: 15,
    abductionDelta: 15,
    goal: "Progress overhead AROM and reduce pain with daily reaching tasks.",
    plan: ["Pendulum Exercise", "Wall Slide"],
  },
  {
    tab: "update",
    status: "ON TRACK",
    statusColor: "#166534",
    phase: "Strengthening",
    pain: 4,
    flexion: 165,
    abduction: 160,
    flexionDelta: 25,
    abductionDelta: 30,
    goal: "Restore full flexion AROM and build abduction strength for lifting.",
    plan: ["Pendulum Exercise", "Wall Slide", "Resisted ER"],
  },
];

const STEP_MS = 2600;

const PATIENTS = [
  { id: "Patient 145", body: "Shoulder", visit: "Initial Evaluation", status: "Completed" },
  { id: "Patient 210", body: "Shoulder", visit: "Initial Evaluation", status: "In Progress" },
  { id: "Patient 694", body: "Knee", visit: "Daily Note", status: "In Progress" },
  { id: "Patient 547", body: "Elbow", visit: "Initial Evaluation", status: "In Progress" },
];

/** Count-up number that animates whenever `value` changes. */
function AnimatedNumber({ value, suffix = "" }: { value: number; suffix?: string }) {
  const mv = useMotionValue(value);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.9,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [value, mv]);

  return (
    <>
      {display}
      {suffix}
    </>
  );
}

export default function JourneyInsightsWidget() {
  const [index, setIndex] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setIndex(SESSIONS.length - 1);
      return;
    }
    timeoutRef.current = window.setTimeout(
      () => setIndex((i) => (i + 1) % SESSIONS.length),
      STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [index]);

  const s = SESSIONS[index];

  // Pain ring geometry
  const R = 22;
  const CIRC = 2 * Math.PI * R;
  const painOffset = CIRC * (1 - s.pain / 10);

  return (
    <div className="relative w-full h-full font-productSans">
      {/* Back — patient list (the broader app behind) */}
      <div className="absolute top-[2%] left-0 w-[66%] h-[93%] rounded-[22px] bg-white shadow-[0_14px_40px_-20px_rgba(31,61,59,0.3)] overflow-hidden z-0">
        <div className="flex items-center justify-between px-3 py-2 bg-[#334155]">
          <span className="text-[10px] font-semibold text-white">Today's Patients</span>
          <span className="text-[8px] text-white/60">4 scheduled</span>
        </div>
        <div className="p-2 flex flex-col gap-1.5">
          {PATIENTS.map((p) => {
            const done = p.status === "Completed";
            return (
              <div key={p.id} className="rounded-lg bg-gray-50 px-2 py-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[9.5px] font-semibold text-gray-800">
                    {p.id}
                  </span>
                  <span
                    className="text-[7.5px] font-medium px-1.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: done ? "#dcfce7" : "#fef3c7",
                      color: done ? "#166534" : "#b45309",
                    }}
                  >
                    {p.status}
                  </span>
                </div>
                <p className="text-[8px] text-gray-400 mt-0.5">
                  {p.body} · {p.visit}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Front — patient insights, hovering on top of the list */}
      <div className="absolute bottom-[2%] right-0 w-[82%] rounded-[24px] bg-white shadow-[0_28px_70px_-14px_rgba(31,61,59,0.5)] overflow-hidden z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#1F3D3B]">
          <span className="flex items-center gap-1.5 text-[12px] font-semibold text-white">
            <Activity className="w-3.5 h-3.5" />
            Patient Insights
          </span>
          <span className="text-[9px] text-white/80">
            Rehab Potential:{" "}
            <span className="font-semibold text-[#7DD3FC]">Very Good</span>
          </span>
        </div>

        {/* Session tabs */}
        <div className="flex items-center gap-1.5 px-4 pt-3">
          {SESSIONS.map((session, i) => {
            const activeTab = i === index;
            const done = i < index;
            return (
              <div
                key={i}
                className={`relative flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] font-medium transition-colors duration-300 ${
                  activeTab
                    ? "bg-[#1F3D3B] text-white"
                    : done
                    ? "bg-[#eef4f1] text-[#495B25]"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    activeTab ? "bg-[#7DD3FC]" : done ? "bg-[#486633]" : "bg-gray-300"
                  }`}
                />
                {session.tab}
              </div>
            );
          })}
          <motion.span
            key={s.status}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="ml-auto text-[8.5px] font-bold tracking-wide px-2 py-1 rounded-full"
            style={{ backgroundColor: `${s.statusColor}1a`, color: s.statusColor }}
          >
            {s.status}
          </motion.span>
        </div>

        <div className="grid grid-cols-2 gap-3 px-4 py-3">
          {/* Subjective pain ring */}
          <div className="flex flex-col items-center justify-center rounded-2xl bg-gray-50 py-3">
            <p className="self-start px-1 mb-1 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
              <Activity className="w-3 h-3" /> Subjective
            </p>
            <div className="relative w-[58px] h-[58px]">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 52 52">
                <circle
                  cx="26"
                  cy="26"
                  r={R}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="5"
                />
                <motion.circle
                  cx="26"
                  cy="26"
                  r={R}
                  fill="none"
                  stroke="#1F3D3B"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={CIRC}
                  animate={{ strokeDashoffset: painOffset }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[16px] font-bold text-gray-900 leading-none">
                  <AnimatedNumber value={s.pain} />
                </span>
                <span className="text-[8px] text-gray-400">/10</span>
              </div>
            </div>
            <p className="text-[8.5px] text-gray-400 mt-1">Pain level</p>
          </div>

          {/* Objective deltas */}
          <div className="flex flex-col gap-2">
            <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
              <TrendingUp className="w-3 h-3" /> Objective
            </p>
            {[
              { label: "Flexion", value: s.flexion, delta: s.flexionDelta },
              { label: "Abduction", value: s.abduction, delta: s.abductionDelta },
            ].map((m) => (
              <div key={m.label} className="rounded-xl bg-gray-50 px-2.5 py-1.5">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[15px] font-bold text-gray-900 leading-none">
                    <AnimatedNumber value={m.value} suffix="°" />
                  </span>
                  {m.delta != null && (
                    <motion.span
                      key={m.delta}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center text-[9px] font-semibold text-[#166534]"
                    >
                      ▲{m.delta}°
                    </motion.span>
                  )}
                </div>
                <p className="text-[8.5px] text-gray-400 mt-0.5">Right {m.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Short-term goal */}
        <div className="px-4 pb-3">
          <p className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            <Target className="w-3 h-3" /> Short-Term Goal
          </p>
          <motion.p
            key={s.goal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="text-[10.5px] leading-snug text-gray-700 rounded-xl bg-gray-50 px-2.5 py-2"
          >
            {s.goal}
          </motion.p>
          {s.plan.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {s.plan.map((item) => (
                <motion.span
                  key={item}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-[#eef4f1] text-[#495B25]"
                >
                  {item}
                </motion.span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
